import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Search, Shield, LogOut, User, Menu, Settings, GraduationCap, ChevronDown, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/useAuth";
import { SignOutConfirmDialog } from "@/components/auth/SignOutConfirmDialog";
import { useEntitlement } from "@/hooks/useEntitlement";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger,
  DropdownMenuSeparator 
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { MobileNav } from "@/components/layout/MobileNav";
import { GlobalSearch } from "@/components/navigation/GlobalSearch";
import { OfflineIndicator } from "@/components/offline/OfflineIndicator";
import { ExamSwitcher } from "@/components/dashboard/ExamSwitcher";

const sb = supabase as any;

export function SiteHeader() {
  const { user, loading, signOut } = useAuth();
  const { isAdmin, isRealAdmin, isImpersonating } = useEntitlement();
  const { viewAsUser, setViewAsUser } = useImpersonation();
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const [showSignOutDialog, setShowSignOutDialog] = useState(false);
  
  // Fetch exams for dropdown
  const { data: exams = [] } = useQuery({
    queryKey: ["header-exams"],
    queryFn: async () => {
      const { data, error } = await sb
        .from("exam_labels")
        .select("id, name, slug")
        .eq("is_active", true)
        .order("display_order", { ascending: true });
      if (error) throw error;
      return data || [];
    },
  });
  
  const handleConfirmSignOut = async () => {
    await signOut();
    navigate('/');
    setShowSignOutDialog(false);
  };
  
  const linkCls = ({ isActive }: { isActive: boolean }) =>
    isActive ? "text-primary font-medium" : "text-foreground/70 hover:text-foreground transition-medical";

  return (
    <>
      {isImpersonating && (
        <div className="sticky top-0 z-50 bg-amber-500 text-amber-950 dark:bg-amber-600 dark:text-amber-50">
          <div className="container mx-auto flex flex-wrap items-center justify-between gap-2 py-1.5 px-4 text-sm">
            <div className="flex items-center gap-2 min-w-0">
              <Eye className="h-4 w-4 shrink-0" />
              <span className="font-medium truncate">Viewing as a regular user</span>
              <Badge variant="outline" className="border-amber-900/40 bg-amber-100/40 text-amber-950 dark:border-amber-50/40 dark:bg-amber-50/10 dark:text-amber-50 hidden sm:inline-flex">
                Admin preview mode
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                className="h-7 border-amber-900/40 bg-amber-100/40 text-amber-950 hover:bg-amber-100/70 dark:border-amber-50/40 dark:bg-amber-50/10 dark:text-amber-50 dark:hover:bg-amber-50/20"
                onClick={() => {
                  setViewAsUser(false);
                  navigate('/admin');
                }}
              >
                <Shield className="mr-1.5 h-3.5 w-3.5" /> Back to Admin
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-7 border-amber-900/40 bg-amber-100/40 text-amber-950 hover:bg-amber-100/70 dark:border-amber-50/40 dark:bg-amber-50/10 dark:text-amber-50 dark:hover:bg-amber-50/20"
                onClick={() => setViewAsUser(false)}
              >
                <EyeOff className="mr-1.5 h-3.5 w-3.5" /> Exit
              </Button>
            </div>
          </div>
        </div>
      )}
      <header className="sticky top-0 z-40 border-b bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <a 
          href="#main-content" 
          className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-1/2 focus:-translate-x-1/2 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-lg focus:shadow-lg"
        >
          Skip to main content
        </a>

        <div className="container flex h-16 items-center justify-between px-6">
          <NavLink to="/" className="flex items-center gap-3 hover-lift">
            <div className="relative">
              <img 
                src="/lovable-uploads/b5752236-32fe-4f98-b1e2-1a6c4f74ec6c.png" 
                alt="Quick Medical Review Logo"
                className="h-10 w-10 object-contain transition-medical hover:scale-105"
              />
            </div>
            <span className="font-bold text-lg text-foreground hidden sm:block">QuickMedicalReview</span>
          </NavLink>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-5" aria-label="Primary navigation">
            {/* Exams Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 text-foreground/70 hover:text-foreground transition-medical text-sm font-medium">
                <GraduationCap className="h-4 w-4" />
                Exams
                <ChevronDown className="h-3 w-3" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56 z-50 bg-background">
                {exams.map((exam: any) => (
                  <DropdownMenuItem key={exam.id} asChild>
                    <NavLink to={user ? `/study?exam=${exam.slug}` : "/pricing"} className="cursor-pointer">
                      {exam.name}
                    </NavLink>
                  </DropdownMenuItem>
                ))}
                {exams.length === 0 && (
                  <DropdownMenuItem disabled>No exams available</DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            {user ? (
              <>
                <NavLink to="/study" className={linkCls} end>Study Hub</NavLink>
                <NavLink to="/dashboard" className={linkCls} end>Dashboard</NavLink>
              </>
            ) : null}
            <NavLink to="/pricing" className={linkCls} end>Pricing</NavLink>
          </nav>

          <div className="flex items-center gap-2">
            {user && <ExamSwitcher />}
            <OfflineIndicator />

            {/* Prominent Admin Dashboard jump — real admins only, hidden while impersonating */}
            {isRealAdmin && !isImpersonating && (
              <Button
                variant="default"
                size="sm"
                onClick={() => navigate('/admin')}
                className="hidden sm:inline-flex"
                title="Go to Admin Dashboard"
              >
                <Shield className="mr-1.5 h-4 w-4" />
                Admin Dashboard
              </Button>
            )}
            
            {/* Global Search */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSearchOpen(true)}
              className="hidden sm:flex"
              title="Search (Cmd+K)"
              aria-label="Open search"
            >
              <Search className="h-5 w-5" />
            </Button>
            
            {loading ? (
              <div className="w-8 h-8 animate-spin border-2 border-primary border-t-transparent rounded-full" />
            ) : user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="secondary" className="hidden sm:inline-flex">
                    <User className="mr-2 h-4 w-4" />
                    {user.email?.split('@')[0]}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 z-50 bg-background">
                  <DropdownMenuItem asChild>
                    <NavLink to="/dashboard" className="cursor-pointer">Dashboard</NavLink>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <NavLink to="/study" className="cursor-pointer">Study Hub</NavLink>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <NavLink to="/pricing" className="cursor-pointer">Pricing</NavLink>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <NavLink to="/settings" className="cursor-pointer">
                      <Settings className="mr-2 h-4 w-4" />
                      Settings
                    </NavLink>
                  </DropdownMenuItem>
                  {isRealAdmin && (
                    <>
                      <DropdownMenuSeparator />
                      {!isImpersonating && (
                        <DropdownMenuItem asChild>
                          <NavLink to="/admin" className="cursor-pointer">
                            <Shield className="mr-2 h-4 w-4" />
                            Admin Panel
                          </NavLink>
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        onClick={() => setViewAsUser(!viewAsUser)}
                        className="cursor-pointer"
                      >
                        {viewAsUser ? (
                          <>
                            <EyeOff className="mr-2 h-4 w-4" />
                            Exit User View
                          </>
                        ) : (
                          <>
                            <Eye className="mr-2 h-4 w-4" />
                            View as User
                          </>
                        )}
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setShowSignOutDialog(true)} className="cursor-pointer">
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button variant="secondary" className="hidden sm:inline-flex" asChild>
                <NavLink to="/auth">
                  <Shield className="mr-2 h-4 w-4" /> Sign in
                </NavLink>
              </Button>
            )}
            
            {/* Mobile Menu */}
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden" aria-label="Open menu">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-80 p-0">
                <MobileNav user={user} onSignOut={() => setShowSignOutDialog(true)} />
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />
      
      <SignOutConfirmDialog
        open={showSignOutDialog}
        onOpenChange={setShowSignOutDialog}
        onConfirm={handleConfirmSignOut}
      />
    </>
  );
}
